import { Component, computed, inject, input, output, signal, TemplateRef, viewChild } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { AdaaHelper } from "../../../../core/utils";
import { Constants } from "../../../constants/constants";
import { Language, PageMode } from "../../../constants/enums";
import { AttachFile, PropTypeModelType, Rule, ValidatorModelType, ValueText } from "../../../models";
import { AppService, PropertiesService, ValidatorApiService } from "../../../services";
import { IntegrityApiService } from "../../../services/integrity-api-service";
import {
  FormActionButtonsComponent,
  FormDropdownComponent,
  FormInputComponent,
  FormInputFileComponent,
} from "../../form";
import { FormInputFormulaComponent } from "../../form/form-input-formula/form-input-formula";

@Component({
  selector: "adaa-add-rule-modal",
  imports: [
    FormInputComponent,
    FormActionButtonsComponent,
    TranslatePipe,
    ReactiveFormsModule,
    FormDropdownComponent,
    FormInputFormulaComponent,
    FormInputFileComponent,
  ],
  templateUrl: "./add-rule-modal.html",
  styleUrl: "./add-rule-modal.scss",
})
export class AddRuleModalComponent {
  private _formBuilder = inject(FormBuilder);
  private _modalService = inject(NgbModal);
  private _appService = inject(AppService);
  private _translateService = inject(TranslateService);
  private _toastrService = inject(ToastrService);
  private _integrityApiService = inject(IntegrityApiService);
  private _propsService = inject(PropertiesService);
  private _validatorApiService = inject(ValidatorApiService);

  addRuleForm: FormGroup = new FormGroup({});
  rule = signal<Rule>({
    id: 0,
    nameAE: "",
    nameEN: "",
    dscAE: "",
    dscEN: "",
    ruleType: 0,
    ruleTypeNameAE: "",
    ruleTypeNameEN: "",
    objectType: 0,
    objectField: 0,
    operatorId: 0,
    objectValue: "",
    errorMessageEN: "",
    errorMessageAE: "",
    expressionStatus: "",
    expression: "",
    fromTableName: "",
    fromFieldName: "",
    toTableName: "",
    toFieldName: "",
    parameterStr: "",
    configId: 0,
    status: 0,
    complexFile: "",
  });
  submitted = signal<boolean>(false);
  modal = viewChild.required<TemplateRef<unknown>>("content");
  modalRef: NgbModalRef;
  ruleTypes = computed(() =>
    AdaaHelper.setDropdownArray(this.ruleTypeOptions(), "id", AdaaHelper.getFieldLanguage("name"))
  );
  ruleTypeOptions = input.required<PropTypeModelType[]>();
  tables = signal<ValueText[]>([]);
  fields = signal<ValueText[]>([]);
  operators = signal<ValueText[]>([]);
  selectedRuleType = signal<number | null>(null);
  formValidation = signal<ValidatorModelType | undefined>(undefined);
  ruleData = output<Rule>();
  pageMode = signal<string>(PageMode.view);
  patched = signal<boolean>(false);
  Constants = Constants;
  PageMode = PageMode;

  private _prepareForm() {
    const isViewMode = this.pageMode() === PageMode.view;
    this.addRuleForm = this._formBuilder.group({
      nameAE: { value: null, disabled: isViewMode },
      nameEN: { value: null, disabled: isViewMode },
      dscAE: { value: null, disabled: isViewMode },
      dscEN: { value: null, disabled: isViewMode },
      errorMessageAE: { value: null, disabled: isViewMode },
      errorMessageEN: { value: null, disabled: isViewMode },
      ruleType: { value: null, disabled: isViewMode },
      objectType: { value: null, disabled: isViewMode },
      objectField: { value: null, disabled: isViewMode },
      operatorId: { value: null, disabled: isViewMode },
      objectValue: { value: null, disabled: isViewMode },
      expression: { value: null, disabled: isViewMode },
      complexFile: { value: null, disabled: isViewMode },
    });

    this._validatorApiService
      .applyValidationToForm(Constants.VALIDATORS_CONF_KEY.VALIDATION_RULES_MANDATORY_CHECK_RULE, this.addRuleForm)
      .subscribe({
        next: (response) => {
          this.addRuleForm = response.form;
          this.formValidation.set(response.validation);
        },
      });
  }

  public open() {
    this._prepareForm();
    this.initData();
    const modalRef = this._modalService.open(this.modal(), {
      animation: true,
      scrollable: false,
      keyboard: false,
      size: "lg",
      centered: true,
      modalDialogClass: this._appService.language() === Language.Arabic ? "modal-rtl" : "modal-ltr",
    });

    this.modalRef = modalRef;
    modalRef.dismissed.subscribe(() => this._resetForm());
    modalRef.closed.subscribe(() => this._resetForm());
  }

  public get file(): AttachFile {
    const attachment = this.addRuleForm.get("complexFile")?.value;
    return {
      id: attachment ? attachment.id : 0,
      filename: attachment ? attachment.filename : "",
    };
  }

  public initData() {
    if (this.pageMode() !== PageMode.create) {
      this.selectedRuleType.set(this.rule().ruleType);
      this.addRuleForm.patchValue(this.rule());
      if (this.rule().ruleType) this._getTables(this.rule().ruleType);
      if (this.rule().objectType) this.getFields(this.rule().objectType);
      if (this.rule().operatorId) this._getOperators();
    }
  }

  private _getTables(id: number) {
    this._integrityApiService.getTables(id).subscribe({
      next: (response) => {
        if (response.inError) return;
        const tables = AdaaHelper.setDropdownArray(response.responseData, "id", AdaaHelper.getFieldLanguage("name"));
        this.tables.set(tables);
      },
    });
  }

  private _getOperators() {
    this._propsService.getPropById(Constants.CONSTANT_RULES_MANAGEMENT_OPERATOR_PROP).subscribe({
      next: (response) => {
        if (response.inError) return;
        const operators = AdaaHelper.setDropdownArray(response.responseData, "id", AdaaHelper.getFieldLanguage("name"));
        this.operators.set(operators);
      },
    });
  }

  public getFields(tableId: number) {
    if (!tableId) return;
    this.addRuleForm.get("objectField")?.reset(null);
    this._integrityApiService.getFields(tableId).subscribe({
      next: (response) => {
        if (response.inError) return;
        const fields = AdaaHelper.setDropdownArray(response.responseData, "id", AdaaHelper.getFieldLanguage("name"));
        this.fields.set(fields);
        if (!this.patched()) {
          this.addRuleForm.get("objectField")?.patchValue(this.rule().objectField);
          this.patched.set(true);
        }
      },
    });
  }

  public onRuleTypeChange(ruleType: ValueText["value"]) {
    this.selectedRuleType.set(ruleType);

    if (
      ruleType === Constants.CONSTANT_INTEGRITY_TABLES_MANDATORY_CHECK ||
      ruleType === Constants.CONSTANT_INTEGRITY_TABLES_VALUE_CHECK
    ) {
      this._getTables(ruleType);
      this.addRuleForm.get("objectField")?.reset();
      this.addRuleForm.get("objectType")?.reset();
      this.addRuleForm.get("objectValue")?.reset();
      this.addRuleForm.get("operatorId")?.reset();
    }

    if (ruleType === Constants.CONSTANT_INTEGRITY_TABLES_VALUE_CHECK) {
      this._getOperators();
    }
  }

  public filechange(event: AttachFile | null): void {
    this.addRuleForm.get("complexFile")?.setValue(event ? event : null);
  }

  public submit() {
    this.submitted.set(true);
    const objectType = this.addRuleForm.get("objectType")?.value;
    const objectField = this.addRuleForm.get("objectField")?.value;

    const isMandatoryCheck = this.selectedRuleType() === Constants.CONSTANT_INTEGRITY_TABLES_MANDATORY_CHECK;

    if (isMandatoryCheck) {
      if (!objectType || !objectField) {
        this._toastrService.error(this._translateService.instant("notification.warning.missing_info"));
        return;
      }
    } else {
      if (this.addRuleForm.invalid) {
        this._toastrService.error(this._translateService.instant("notification.warning.missing_info"));
        return;
      }
    }

    const formValue = this.addRuleForm.value;
    const type = this.ruleTypeOptions().find((t) => t.id === formValue.ruleType);
    const res = {
      ...(this.pageMode() === PageMode.edit ? { ...this.rule() } : {}),
      ...this.addRuleForm.value,
      ruleTypeNameAE: type?.nameAE,
      ruleTypeNameEN: type?.nameEN,
    };
    this.ruleData.emit(res);
    this.modalRef.close();
  }

  public checkInvalid(control: string) {
    const result =
      this.addRuleForm.controls[control]?.errors?.["required"] &&
      (this.addRuleForm.get(control)?.dirty || this.submitted());
    return result;
  }

  private _resetForm() {
    this.patched.set(false);
    this.addRuleForm.reset();
    this.addRuleForm.markAsPristine();
    this.addRuleForm.markAsUntouched();
    this.submitted.set(false);
  }
}
